/**
 * Thin fetch wrapper for our Pages Functions API.
 * v9: switched from localStorage Bearer token → HttpOnly session cookie.
 * - All fetches use `credentials: 'include'` so the cookie rides along
 * - 401 still emits 'auth:expired' so AuthProvider can react
 * - Migration: any leftover Bearer-token localStorage key is cleared on import
 */

// One-time legacy cleanup so users who still have a v8 token don't carry it.
const LEGACY_KEY = 'speedrun-token';
if (typeof window !== 'undefined') {
  try { window.localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip emitting 'auth:expired' on 401 (used by login endpoints). */
  noAuth?: boolean;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers: { 'content-type': 'application/json' },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'include',
  });

  if (res.status === 401 && !opts.noAuth) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    if (!res.ok) throw new ApiError(res.status, 'http_error', `HTTP ${res.status}`);
    return undefined as T;
  }

  if (!res.ok) {
    const e = (data as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(res.status, e?.code ?? 'unknown', e?.message ?? `HTTP ${res.status}`);
  }
  return data as T;
}
